import { computed, ref } from 'vue'

/**
 * keyset 游标分页的页面侧状态：当前页数据、回退栈、下一页游标、加载态。
 * 后端只给 `next_cursor`，「上一页」靠页面自己记住走过的游标；第一页的游标是 null。
 * 筛选条件变了调 `reset()` 回到第一页；`fetchPage(cursor)` 由页面提供，返回 `{ items, next_cursor }`。
 * @template T
 * @param {(cursor: string|null) => Promise<{ items: T[], next_cursor: string|null }>} fetchPage
 */
export function useCursorPagination(fetchPage) {
  const items = ref(/** @type {T[]} */ ([]))
  const loading = ref(false)
  const error = ref('')
  const currentCursor = ref(/** @type {string|null} */ (null))
  const nextCursor = ref(/** @type {string|null} */ (null))
  const cursorStack = ref(/** @type {Array<string|null>} */ ([]))

  async function load(cursor) {
    loading.value = true
    error.value = ''
    currentCursor.value = cursor
    try {
      const page = await fetchPage(cursor)
      items.value = page.items
      nextCursor.value = page.next_cursor
    } catch (cause) {
      error.value = cause.message
    } finally {
      loading.value = false
    }
  }

  return {
    items,
    loading,
    error,
    hasPrev: computed(() => cursorStack.value.length > 0),
    hasNext: computed(() => nextCursor.value !== null),
    pageNumber: computed(() => cursorStack.value.length + 1),
    reset() {
      cursorStack.value = []
      return load(null)
    },
    reload() {
      return load(currentCursor.value)
    },
    next() {
      cursorStack.value = [...cursorStack.value, currentCursor.value]
      return load(nextCursor.value)
    },
    previous() {
      const stack = [...cursorStack.value]
      const cursor = stack.pop()
      cursorStack.value = stack
      return load(cursor)
    },
  }
}
